/** Filter state management hook. */

import { useState, useCallback } from 'preact/hooks';

export interface FilterState {
  categories: Set<string>;
  certifications: Set<string>;
  manufacturers: Set<string>;
  inStockOnly: boolean;
  priceMin: string;
  priceMax: string;
}

export const EMPTY_FILTERS: FilterState = {
  categories: new Set(),
  certifications: new Set(),
  manufacturers: new Set(),
  inStockOnly: false,
  priceMin: '',
  priceMax: '',
};

export function useFilters() {
  const [filters, setFilters] = useState<FilterState>({
    ...EMPTY_FILTERS,
    categories: new Set(),
    certifications: new Set(),
    manufacturers: new Set(),
  });

  const updateFilters = useCallback((updates: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      ...EMPTY_FILTERS,
      categories: new Set(),
      certifications: new Set(),
      manufacturers: new Set(),
    });
  }, []);

  const toggleCategory = useCallback((category: string) => {
    setFilters((prev) => {
      const next = new Set(prev.categories);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return { ...prev, categories: next };
    });
  }, []);

  const toggleCertification = useCallback((cert: string) => {
    setFilters((prev) => {
      const next = new Set(prev.certifications);
      if (next.has(cert)) {
        next.delete(cert);
      } else {
        next.add(cert);
      }
      return { ...prev, certifications: next };
    });
  }, []);

  const toggleManufacturer = useCallback((manufacturer: string) => {
    setFilters((prev) => {
      const next = new Set(prev.manufacturers);
      if (next.has(manufacturer)) {
        next.delete(manufacturer);
      } else {
        next.add(manufacturer);
      }
      return { ...prev, manufacturers: next };
    });
  }, []);

  const hasActiveFilters = filters.categories.size > 0
    || filters.certifications.size > 0
    || filters.manufacturers.size > 0
    || filters.inStockOnly
    || filters.priceMin !== ''
    || filters.priceMax !== '';

  return {
    filters,
    updateFilters,
    clearFilters,
    toggleCategory,
    toggleCertification,
    toggleManufacturer,
    hasActiveFilters,
  };
}
